import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DataGridCustomPagination } from "components/helpers";
import UserDetails from "../userDetails";
import { getUsers, searchUsers } from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";

import { columns, detailsColumns } from "./helper";
import ResetPasswordModal from "../resetPassword";

const UserAdvanceSearch = ({ open, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openUser, setOpenUser] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [data, setData] = useState([]);
  const [form, setForm] = useState({
    user_id: "",
    first_name: "",
    last_name: "",
  });
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [openReset, setOpenReset] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const handleOpenResetPassword = (user) => {
    setResetUser(user);
    setOpenReset(true);
  };

  const handleCloseResetPassword = () => {
    setOpenReset(false);
    setResetUser(null);
  };

  const userData = useUserData();
  const token = userData?.access_token;

  const showSnack = (message, severity = "info") => {
    setSnack({ open: true, message, severity });
  };

  const handleSnackClose = () => {
    setSnack({ ...snack, open: false });
  };

  const handleViewDetails = async (user) => {
    setOpenUser(true);
    try {
      setDetailsLoading(true);
      const res = await getUsers({ user_id: user.user_id }, token);
      if (res) {
        setUserDetails(res);
      }
    } catch (error) {
      console.log("Error fetching user details:", error);
      showSnack("Failed to fetch user details", "error");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleOpenDetailsModal = (user) => {
    setSelectedUser(user);
    setOpenDetails(true);
  };

  const handleCloseDetailsModal = () => {
    setOpenDetails(false);
    setSelectedUser(null);
  };

  const handleCloseUserDetailsModal = () => {
    setOpenUser(false);
    setUserDetails(null);
  };

  const onSearch = async () => {
    setLoading(true);
    const { user_id, first_name, last_name } = form;

    if (!user_id && !first_name && !last_name) {
      showSnack("Please enter at least one field to search.", "warning");
      return;
    }

    const payload = {};
    if (user_id) payload.user_id = user_id;
    if (first_name) payload.first_name = first_name;
    if (last_name) payload.last_name = last_name;

    try {
      const res = await searchUsers(payload, token);
      if (res && res.length > 0) {
        setData(res);
      }
    } catch (error) {
      console.error("Search error: ", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="vw" fullWidth>
      <DialogTitle
        sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between" }}
      >
        Search for a User
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent dividers>
        <Box display={"flex"} alignItems={"center"} gap={2}>
          <TextField
            fullWidth
            label="Email"
            size="small"
            value={form.user_id}
            onChange={(e) => setForm({ ...form, user_id: e.target.value })}
          />
          <TextField
            fullWidth
            label="First Name"
            size="small"
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Last Name"
            size="small"
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          />
          <Button variant="outlined" size="small" onClick={onSearch}>
            Search
          </Button>
        </Box>
        {loading ? (
          <Box display={"flex"} justifyContent={"center"} m={2} mt={4}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <Box mt={4}>
            <DataGrid
              rows={data}
              columns={columns(handleViewDetails, handleOpenResetPassword)}
              pageSize={10}
              autoHeight
              slots={{
                pagination: DataGridCustomPagination,
                toolbar: GridToolbar,
              }}
            />
          </Box>
        )}
      </DialogContent>
      <UserDetails
        openDetails={openDetails}
        handleCloseDetailsModal={handleCloseDetailsModal}
        selectedUser={selectedUser}
      />

      <Dialog
        open={openUser}
        onClose={handleCloseUserDetailsModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          User Details
          <IconButton onClick={handleCloseUserDetailsModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box mt={2}>
            <DataGrid
              rows={userDetails}
              columns={detailsColumns(handleOpenDetailsModal)}
              pageSize={10}
              autoHeight
              slots={{
                pagination: DataGridCustomPagination,
                toolbar: GridToolbar,
              }}
              loading={detailsLoading}
              slotProps={{
                loadingOverlay: {
                  variant: "linear-progress",
                  noRowsVariant: "linear-progress",
                },
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
      <ResetPasswordModal
        open={openReset}
        onClose={handleCloseResetPassword}
        user={resetUser}
      />
    </Dialog>
  );
};

export default UserAdvanceSearch;
